import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
  useMediaQuery,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';

const PricingPlans = () => {
  const [annual, setAnnual] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handlePricingChange = () => {
    setAnnual(!annual);
  };

  const plans = [
    {
      title: 'Plan Básico',
      subtitle: 'Para talleres pequeños',
      price: annual ? 199900 : 19990,
      period: annual ? '/año' : '/mes',
      savings: annual ? 'Ahorra 2 meses' : '',
      description: 'Ideal para talleres con un máximo de 3 mecánicos y gestión básica de servicios.',
      buttonText: 'Comenzar ahora',
      buttonVariant: 'outlined',
      features: [
        {
          name: 'Hasta 3 usuarios',
          included: true,
        },
        {
          name: 'Registro de vehículos ilimitado',
          included: true,
        },
        {
          name: 'Fichas de servicio básicas',
          included: true,
        },
        {
          name: 'Gestión de clientes',
          included: true,
        },
        {
          name: 'Reportes básicos',
          included: true,
        },
        {
          name: 'Agenda y calendario',
          included: true,
        },
        {
          name: 'Módulo de repuestos',
          included: false,
        },
        {
          name: 'API para integraciones',
          included: false,
        },
        {
          name: 'Soporte prioritario',
          included: false,
        },
        {
          name: 'Copia de seguridad diaria',
          included: false,
        },
      ],
      popular: false,
      color: '#1976d2',
    },
    {
      title: 'Plan Profesional',
      subtitle: 'Para talleres medianos y grandes',
      price: annual ? 349900 : 34990,
      period: annual ? '/año' : '/mes',
      savings: annual ? 'Ahorra 2 meses' : '',
      description: 'Solución completa para talleres con hasta 10 mecánicos y gestión avanzada.',
      buttonText: 'Comenzar ahora',
      buttonVariant: 'contained',
      features: [
        {
          name: 'Hasta 10 usuarios',
          included: true,
        },
        {
          name: 'Registro de vehículos ilimitado',
          included: true,
        },
        {
          name: 'Fichas de servicio avanzadas',
          included: true,
        },
        {
          name: 'Gestión de clientes',
          included: true,
        },
        {
          name: 'Reportes avanzados y exportables',
          included: true,
        },
        {
          name: 'Agenda y calendario',
          included: true,
        },
        {
          name: 'Módulo de repuestos completo',
          included: true,
        },
        {
          name: 'API para integraciones',
          included: true,
        },
        {
          name: 'Soporte prioritario',
          included: true,
        },
        {
          name: 'Copia de seguridad diaria',
          included: true,
        },
      ],
      popular: true,
      color: '#f50057',
    },
  ];

  return (
    <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            variant="overline"
            color="primary"
            sx={{ fontWeight: 600, letterSpacing: 1.2 }}
          >
            PLANES Y PRECIOS
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            variant="h3"
            sx={{ mt: 1, mb: 2, fontWeight: 700 }}
          >
            Elige el plan que mejor se adapte a tu taller
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto', fontWeight: 400, mb: a => theme.spacing(a ? 6 : 2) }}
          >
            Planes pensados para optimizar los procesos de tu taller mecánico.
          </Typography>

          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 6 
            }}
          >
            <Typography variant="body1" color="text.secondary">Mensual</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={annual}
                  onChange={handlePricingChange}
                  color="primary"
                  name="pricingPeriod"
                />
              }
              label=""
              sx={{ mx: 2 }}
            />
            <Typography variant="body1" color="text.secondary">Anual</Typography>
            <Chip
              label="Ahorra hasta 20%"
              color="primary"
              size="small"
              sx={{ ml: 2, fontWeight: 500 }}
            />
          </Box>
        </Box>

        <Grid 
          container 
          spacing={4} 
          justifyContent="center"
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          viewport={{ once: true }}
        >
          {plans.map((plan, index) => (
            <Grid item key={index} xs={12} md={6} lg={5}>
              <Card
                elevation={plan.popular ? 8 : 1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  border: plan.popular ? `2px solid ${plan.color}` : 'none',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: plan.popular ? '0 16px 30px rgba(0, 0, 0, 0.15)' : '0 10px 20px rgba(0, 0, 0, 0.1)',
                  },
                  borderRadius: '12px',
                }}
              >
                {plan.popular && (
                  <Chip
                    label="MÁS POPULAR"
                    color="secondary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 600,
                    }}
                  />
                )}
                <CardHeader
                  title={plan.title}
                  subheader={plan.subtitle}
                  titleTypographyProps={{ align: 'center', fontWeight: 700 }}
                  subheaderTypographyProps={{ align: 'center' }}
                  sx={{ bgcolor: `${plan.color}10`, pt: 4 }}
                />
                <CardContent sx={{ flexGrow: 1, px: 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography component="p" variant="subtitle2" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'baseline',
                      mb: 2,
                    }}
                  >
                    <Typography component="h2" variant="h3" color="text.primary">
                      CLP ${plan.price.toLocaleString('es-CL')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>
                  {plan.savings && (
                    <Typography variant="subtitle2" align="center" color="success.main" gutterBottom>
                      {plan.savings}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <List>
                    {plan.features.map((feature, featureIdx) => (
                      <ListItem dense disableGutters key={featureIdx}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {feature.included ? (
                            <CheckIcon sx={{ color: 'success.main' }} />
                          ) : (
                            <CloseIcon color="disabled" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.name}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            color: feature.included ? 'textPrimary' : 'textSecondary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={plan.buttonVariant}
                    color={plan.popular ? 'secondary' : 'primary'}
                    sx={{ py: 1.5 }}
                  >
                    {plan.buttonText}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          viewport={{ once: true }}
          sx={{
            textAlign: 'center',
            mt: 8,
            p: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            ¿Necesitas una solución personalizada?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Contacta con nuestro equipo para obtener un plan adaptado a las necesidades específicas de tu taller.
          </Typography>
          <Button variant="outlined" color="primary">
            Contactar para plan personalizado
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingPlans; 